import React, { useEffect, useState, useCallback } from "react";
import { Sparkles, Loader2, RotateCcw, ExternalLink, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { SITEDETAIL } from "@/constants/testIds";

/**
 * AI Legal Rewriter — paraphrases Privacy / Terms / CCPA with Claude Haiku 4.5
 * for a second layer of anti-network-footprint structural diversity.
 *
 * Each document is one of three states:
 *   • auto         — pure procedural generator (default)
 *   • ai_rewritten — Markdown body persisted on the site doc via this panel
 *   • operator     — plain-text operator paste (no `## ` headings)
 *
 * Cost: ~$0.005-0.010 per document (Haiku 4.5 pricing). The button is disabled
 * when the per-site daily AI budget is exhausted — same guard the article
 * rewrite pipeline uses.
 */

const DOC_LABEL = {
  privacy: "Privacy Policy",
  terms: "Terms of Service",
  ccpa: "California Privacy Notice (CCPA)",
};

const DOC_PATH = {
  privacy: "privacy",
  terms: "terms",
  ccpa: "ccpa",
};

const SOURCE_LABEL = {
  auto: "Auto-generated",
  ai_rewritten: "AI-rewritten",
  operator: "Operator-pasted",
};

const SOURCE_PILL = {
  auto: "bg-zinc-800/60 border-zinc-700 text-zinc-300",
  ai_rewritten: "bg-violet-500/15 border-violet-500/40 text-violet-200",
  operator: "bg-amber-500/15 border-amber-500/40 text-amber-200",
};

export default function LegalRewriterPanel({ site }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // `${kind}` or "all" or "reset:${kind}"
  const [flash, setFlash] = useState(null);
  const [lastRun, setLastRun] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/sites/${site.id}/legal/status`);
      return r.data;
    } catch (e) {
      throw e;
    }
  }, [site.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await load();
        if (!cancelled) setStatus(data);
      } catch (e) {
        if (!cancelled) setFlash({ kind: "err", msg: e?.response?.data?.detail || "Failed to load legal status" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [load]);

  async function refresh() {
    try {
      const data = await load();
      setStatus(data);
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "Refresh failed" });
    }
  }

  const remaining = status?.ai_budget?.remaining_usd ?? 0;
  const budgetExhausted = remaining <= 0;

  async function rewrite(kind) {
    if (busy) return;
    setBusy(kind);
    setFlash(null);
    try {
      const r = await api.post(`/sites/${site.id}/legal/ai-rewrite`, { kind });
      setLastRun(r.data);
      const label = kind === "all" ? "all 3 documents" : DOC_LABEL[kind];
      setFlash({
        kind: "ok",
        msg: `${label} rewritten by Claude Haiku 4.5 — $${r.data.total_cost_usd.toFixed(4)} spent`,
      });
      await refresh();
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "AI rewrite failed" });
    } finally {
      setBusy(null);
    }
  }

  async function reset(kind) {
    const tag = `reset:${kind}`;
    if (busy) return;
    setBusy(tag);
    setFlash(null);
    try {
      await api.post(`/sites/${site.id}/legal/reset`, { kind });
      const label = kind === "all" ? "all 3 documents" : DOC_LABEL[kind];
      setFlash({ kind: "ok", msg: `${label} reverted to the auto-generated default` });
      await refresh();
    } catch (e) {
      setFlash({ kind: "err", msg: e?.response?.data?.detail || "Reset failed" });
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return (
      <div className="border border-zinc-800 rounded-sm p-5 bg-zinc-900/30">
        <div className="text-xs text-zinc-500 mono inline-flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading legal-page status…
        </div>
      </div>
    );
  }

  return (
    <div className="border border-zinc-800 rounded-sm p-5 bg-zinc-900/30 space-y-4" data-testid="sitedetail-legal-rewriter-panel">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="font-display text-lg font-bold inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-violet-300" />
            Legal pages — AI paraphrase
          </div>
          <div className="text-xs text-zinc-500 mono mt-1 leading-relaxed">
            Claude Haiku 4.5 lightly rewrites Privacy / Terms / CCPA on top of the procedural generator
            for a second layer of anti-footprint diversity. Same legal substance, fresh wording. ~$0.005-0.010 per doc.
          </div>
        </div>
        <div className="text-[11px] mono text-zinc-400 shrink-0">
          AI budget today: <span className={budgetExhausted ? "text-rose-400" : "text-emerald-400"}>${remaining.toFixed(2)} left</span>
        </div>
      </div>

      <div className="border border-zinc-800 rounded-sm overflow-hidden">
        {status?.documents?.map((doc) => (
          <div
            key={doc.kind}
            className="flex items-center justify-between gap-3 px-3 py-2.5 border-b border-zinc-800 last:border-b-0"
          >
            <div className="min-w-0">
              <div className="text-sm text-zinc-200 truncate">{DOC_LABEL[doc.kind]}</div>
              <div className="text-[11px] mono text-zinc-500 mt-0.5 flex items-center gap-2">
                <span
                  data-testid={SITEDETAIL.legalStatusBadge(doc.kind)}
                  className={`inline-block border rounded-full px-2 py-px text-[10px] ${SOURCE_PILL[doc.source] || SOURCE_PILL.auto}`}
                >
                  {SOURCE_LABEL[doc.source] || doc.source}
                </span>
                {doc.length > 0 && <span>{doc.length.toLocaleString()} chars stored</span>}
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/render/sites/${site.slug}/${DOC_PATH[doc.kind]}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={SITEDETAIL.legalPreviewLink(doc.kind)}
                  className="text-amber-400 hover:underline inline-flex items-center gap-1"
                >
                  Preview <ExternalLink className="w-2.5 h-2.5" />
                </a>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                data-testid={SITEDETAIL.legalRewriteBtn(doc.kind)}
                onClick={() => rewrite(doc.kind)}
                disabled={!!busy || budgetExhausted}
                title={budgetExhausted ? "Daily AI budget exhausted" : `Rewrite ${DOC_LABEL[doc.kind]} with Claude Haiku`}
                className="bg-violet-500/20 border border-violet-500/40 hover:bg-violet-500/30 text-violet-100 px-2.5 py-1 rounded-sm text-[11px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy === doc.kind ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Rewrite
              </button>
              {doc.source !== "auto" && (
                <button
                  data-testid={SITEDETAIL.legalResetBtn(doc.kind)}
                  onClick={() => reset(doc.kind)}
                  disabled={!!busy}
                  title={`Revert ${DOC_LABEL[doc.kind]} to the auto-generated default`}
                  className="border border-zinc-700 hover:border-zinc-500 text-zinc-400 hover:text-zinc-200 px-2 py-1 rounded-sm text-[11px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
                >
                  {busy === `reset:${doc.kind}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                  Reset
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          data-testid={SITEDETAIL.legalRewriteBtn("all")}
          onClick={() => rewrite("all")}
          disabled={!!busy || budgetExhausted}
          className="bg-violet-500/20 border border-violet-500/40 hover:bg-violet-500/30 text-violet-100 px-3 py-1.5 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy === "all" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          Rewrite all 3 (~$0.02)
        </button>
        {status?.documents?.some((d) => d.source !== "auto") && (
          <button
            data-testid={SITEDETAIL.legalResetBtn("all")}
            onClick={() => reset("all")}
            disabled={!!busy}
            className="border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-3 py-1.5 rounded-sm text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
          >
            {busy === "reset:all" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Reset all
          </button>
        )}
      </div>

      {lastRun && (
        <div className="border border-zinc-800 rounded-sm overflow-hidden">
          <div className="px-3 py-2 bg-zinc-900/60 text-[11px] text-zinc-400 mono border-b border-zinc-800 flex items-center justify-between">
            <span>LAST AI RUN</span>
            <span>+${lastRun.total_cost_usd.toFixed(4)} · {lastRun.rewrote.length} doc(s)</span>
          </div>
          <div className="divide-y divide-zinc-800">
            {lastRun.rewrote.map((r) => (
              <div key={r.kind} className="px-3 py-2 text-[11px] mono flex items-center justify-between">
                <span className="text-zinc-300">{DOC_LABEL[r.kind]}</span>
                <span className="text-zinc-500">
                  {r.tokens_in.toLocaleString()} in · {r.tokens_out.toLocaleString()} out · ${r.cost_usd.toFixed(4)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {flash && (
        <div
          data-testid="sitedetail-legal-flash"
          className={`text-xs mono ${flash.kind === "ok" ? "text-emerald-400" : "text-rose-400"}`}
        >
          {flash.kind === "ok" ? "✓ " : "✕ "}{flash.msg}
        </div>
      )}
    </div>
  );
}
